import { setIntegrationData } from '@/atoms/integrationAtoms';
import { useLatestRef } from '@/hooks/useLatestRef';
import {
  type MedicalDirectorSidebar,
  PropertyBagValue,
} from '@/integrations/medical-director/medicalDirectorSidebar';
import { isValidEHRPatientId } from '@/lib/utils';
import { logger } from '@/utils/logger/logger';
import { ConsultNoteStatus, EHRProvider } from '@heidi/schema/ml-scribe';
import { useEffect } from 'react';
import { type MdSession } from './types';
import { type PendingMdPatient } from './usePendingMdPatient';

type UseMdPatientSyncParams = {
  medicalDirector: MedicalDirectorSidebar | null;
  pendingPatient: PendingMdPatient;
  sessionId: string | undefined;
  session: MdSession;
  integrationPatientId: string | undefined;
  isUpdatingPatientDetails: boolean;
  isUpdatingSessionEHRDetails: boolean;
  updateSessionEHRDetails: (params: {
    sessionId: string;
    provider: EHRProvider;
    patientId: string;
  }) => void;
  onSwitchPatient: (newPatientId: string) => void;
};

export const useMdPatientSync = ({
  medicalDirector,
  pendingPatient,
  sessionId,
  session,
  integrationPatientId,
  isUpdatingPatientDetails,
  isUpdatingSessionEHRDetails,
  updateSessionEHRDetails,
  onSwitchPatient,
}: UseMdPatientSyncParams) => {
  // Read the latest values inside the effect via refs so we can keep deps
  // narrow (matching the original effect's intent of triggering only on the
  // listed dep changes).
  const integrationPatientIdRef = useLatestRef(integrationPatientId);
  const onSwitchPatientRef = useLatestRef(onSwitchPatient);

  useEffect(() => {
    if (!medicalDirector || !sessionId || !session) return;

    const currentPatientId =
      pendingPatient.get() ||
      integrationPatientIdRef.current ||
      medicalDirector.readPropertyBagValue(PropertyBagValue.CURRENT_PATIENT_ID);

    if (!isValidEHRPatientId(currentPatientId)) return;

    const currentPatientIdString = String(currentPatientId);
    const sessionEhrPatientId = String(session.ehr_patient_id);

    pendingPatient.clearIfMatches(sessionEhrPatientId);

    const updatingPatientDetails =
      isUpdatingSessionEHRDetails || isUpdatingPatientDetails;

    const shouldUpdatePatientDetails =
      !session.ehr_patient_id &&
      !updatingPatientDetails &&
      session.consult_note?.status === ConsultNoteStatus.CREATED;

    if (shouldUpdatePatientDetails) {
      logger.debug('[MD]', 'Update session EHR details');

      updateSessionEHRDetails({
        sessionId,
        provider: EHRProvider.Medical_Director,
        patientId: currentPatientIdString,
      });

      medicalDirector.send('GetDemographicsRequest', {
        PatientID: Number(currentPatientId),
      });

      setIntegrationData({
        patientId: currentPatientIdString,
      });
    } else if (sessionEhrPatientId !== currentPatientIdString) {
      if (pendingPatient.get() === currentPatientIdString) {
        logger.debug(
          '[MD]',
          'Skipping patient switch while syncing MD-opened patient',
          currentPatientIdString
        );
        return;
      }

      logger.debug('[MD]', 'Switching patient');
      onSwitchPatientRef.current(sessionEhrPatientId);
    }
  }, [
    medicalDirector,
    sessionId,
    session,
    updateSessionEHRDetails,
    isUpdatingSessionEHRDetails,
    isUpdatingPatientDetails,
    onSwitchPatientRef,
    integrationPatientIdRef,
    pendingPatient,
  ]);
};
